import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ONE_MINUTE_MS } from '../../../../../pure-js/libs/Consts'
import { resolveWherebyCallKind } from '../../../../../pure-js/libs/wherebyCallOwnership'
import type { SentToActivityPayload } from '../../../../../pure-js/types/InAppNotification'
import { useMarkNotificationViewed } from '../../../apiLayer/queryHooks/mutations'
import { resolveCallRoute } from '../../../shared/hooks/wherebyCallScopes'
import { useAppSelector } from '../../../store'
import { selectUnviewedSentToActivity } from '../../../store/selectors/inAppNotificationsSelectors'

/** A send is followed while it is fresh, so someone who opens the app much later is not moved by it. */
export const SENT_TO_ACTIVITY_FOLLOW_WINDOW_MS = 10 * ONE_MINUTE_MS // 10 min

/** Takes the player to the page of the game or debrief their facilitator last sent the team to, once per send. */
export const useGoToActivityWhenSent = () => {
  const unviewedSends = useAppSelector(selectUnviewedSentToActivity)
  const { mutate: markViewed } = useMarkNotificationViewed()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const followedSendRef = useRef<string | null>(null)

  useEffect(() => {
    const [sent] = unviewedSends
    if (!sent) return

    // A second send replaces the first under the same id, so the moment it was sent is part of what makes it new.
    const sendKey = `${sent.id}@${sent.createdAt}`
    if (followedSendRef.current === sendKey) return
    followedSendRef.current = sendKey

    // Only the newest send is followed; one still waiting from before it is overtaken, not followed after it.
    unviewedSends.forEach((waitingSend) => markViewed(waitingSend.id))

    const { activityType } = sent.payload as SentToActivityPayload
    const activityPage = resolveCallRoute(resolveWherebyCallKind(activityType), pathname)
    const isFresh = Date.now() - new Date(sent.createdAt).getTime() <= SENT_TO_ACTIVITY_FOLLOW_WINDOW_MS
    const isElsewhere = !pathname.startsWith(activityPage)
    const shouldGoThere = isFresh && isElsewhere
    if (shouldGoThere) navigate(activityPage)
  }, [unviewedSends, pathname, markViewed, navigate])
}
